import { PARTY_DUR } from './partyDance';

export type ChargeState = 'off' | 'booting' | 'charging' | 'shutdown' | 'party';

/** Battery level the charge screen starts and ends at */
export const CHARGE_LOW = 0.1;
/** Full from low in ~2.5s of steady clicking; drains at the same speed when you stop */
const CHARGE_RATE = (1 - CHARGE_LOW) / 2.5;
/** One click keeps the charger "plugged in" this long (s) — so ~2+ clicks/s keeps it climbing */
const CLICK_HOLD = 0.45;
/** Face ↔ battery scan-wipe duration (s) */
export const CHARGE_WIPE = 0.45;
/** Three face clicks within this window (s) start charging */
const TRIPLE_WINDOW = 0.9;

/**
 * Easter egg: triple-click Pixel's face → battery screen. Keep clicking to charge it;
 * stop and it drains back to low and switches off. Full charge → party.
 */
export class ChargeMode {
  state: ChargeState = 'off';
  level = CHARGE_LOW;
  /** 0..1 flash from the latest click */
  impulse = 0;
  stateAt = 0;
  private lastClick = -10;
  private faceClicks: number[] = [];
  private rate: number;
  /** Sticky: draining to low never switches the battery screen off (the quest's take-off charge) */
  private sticky: boolean;

  /** @param opts.seconds time from low to full with steady clicking */
  constructor(opts: { seconds?: number; sticky?: boolean } = {}) {
    this.rate = opts.seconds ? (1 - CHARGE_LOW) / opts.seconds : CHARGE_RATE;
    this.sticky = !!opts.sticky;
  }

  /** Open the battery screen right away (no triple-click needed) */
  begin(t: number) {
    this.level = CHARGE_LOW;
    this.lastClick = -10;
    this.impulse = 0;
    this.faceClicks = [];
    this.set('booting', t);
  }

  /**
   * Handle a click. Returns true when charge mode consumed it.
   * @param onFace click landed on the face display
   * @param onBody click landed anywhere on the drone
   */
  click(t: number, onFace: boolean, onBody: boolean): boolean {
    if (this.state === 'party') return onBody;
    if (this.state === 'booting' || this.state === 'charging' || this.state === 'shutdown') {
      if (!onBody) return false;
      if (this.state === 'shutdown') this.set('charging', t);
      this.lastClick = t;
      this.impulse = 1;
      return true;
    }
    if (!onFace) {
      this.faceClicks = [];
      return false;
    }
    this.faceClicks = this.faceClicks.filter((c) => t - c < TRIPLE_WINDOW);
    this.faceClicks.push(t);
    if (this.faceClicks.length < 3) return false;
    this.faceClicks = [];
    this.level = CHARGE_LOW;
    this.lastClick = t;
    this.impulse = 1;
    this.set('booting', t);
    return true;
  }

  update(t: number, dt: number) {
    this.impulse *= Math.exp(-dt * 8);
    const active = this.isPlugged(t);
    switch (this.state) {
      case 'booting':
        if (t - this.stateAt >= CHARGE_WIPE) this.set('charging', t);
        break;
      case 'charging':
        this.level += (active ? 1 : -1) * this.rate * dt;
        if (this.level >= 1) {
          this.level = 1;
          this.set('party', t);
        } else if (!this.sticky && this.level <= CHARGE_LOW && !active && t - this.stateAt > 0.6) {
          this.level = CHARGE_LOW;
          this.set('shutdown', t);
        }
        this.level = Math.max(this.level, CHARGE_LOW * 0.5);
        break;
      case 'shutdown':
        if (t - this.stateAt >= CHARGE_WIPE) this.set('off', t);
        break;
      case 'party':
        if (t - this.stateAt >= PARTY_DUR) {
          this.level = CHARGE_LOW;
          this.set('off', t);
        }
        break;
      default:
        break;
    }
  }

  /** Clicked recently enough to count as charging */
  isPlugged(t: number) {
    return t - this.lastClick < CLICK_HOLD;
  }

  /** 0 = normal face, 1 = battery screen fully shown (drives the scan-line wipe) */
  wipe(t: number): number {
    const k = (t - this.stateAt) / CHARGE_WIPE;
    switch (this.state) {
      case 'booting':
        return Math.min(1, k);
      case 'charging':
        return 1;
      case 'shutdown':
        return Math.max(0, 1 - k);
      case 'party':
        // Hold "100%" for a beat, then wipe back to the (very happy) face
        return Math.max(0, Math.min(1, 1 - (t - this.stateAt - 0.35) / CHARGE_WIPE));
      default:
        return 0;
    }
  }

  reset(t: number) {
    this.level = CHARGE_LOW;
    this.impulse = 0;
    this.faceClicks = [];
    this.set('off', t);
  }

  private set(state: ChargeState, t: number) {
    this.state = state;
    this.stateAt = t;
  }
}
